<template>
    <section class="w-100" v-if="itemForm">

      <div class="row">
        <!-- Header -->
        <div class="header col-12">
          <div class="header-body">
            <div class="row align-items-end">
              <div class="col">
                <h1 class="header-title">{{ heading }}</h1>
              </div>
              <div class="col-auto">
                <div class="row align-items-center">
                  <div class="col-auto" v-if="itemForm.status">
                    <span class="small">Current Status</span>
                    <h5><OrderReturnStatusBadge :status="itemForm.status" /></h5>
                  </div>
                  <div class="col-auto" v-if="itemForm.createdAt">
                    <span class="small">Created At</span>
                    <h5>{{ itemForm.createdAt }}</h5>
                  </div>
                  <div class="col-auto" v-if="itemForm.orderNumber">
                    <span class="small">Order Number</span>
                    <h5>
                      {{ itemForm.orderNumber }}
                      <router-link
                        v-if="itemForm.orderId"
                        :to="{ name: 'Common.OrderDetails', query: { id: itemForm.orderId } }"
                        class="ml-1 text-muted"
                        v-b-tooltip.hover
                        title="View Order"
                      >
                        <i class="fa fa-external-link"></i>
                      </router-link>
                    </h5>
                  </div>
                  <div class="col-auto">
                    <form @submit.prevent data-vv-scope="itemForm">
                      <span class="small">Returned On</span>
                      <b-input-group size="sm">
                        <b-form-input
                          v-validate="{ required: true }"
                          id="form-input-returnDate"
                          name="form-input-returnDate"
                          v-model="itemForm.receiveDate"
                          type="date"
                        />
                        <b-input-group-append>
                          <b-form-datepicker
                            :reset-button="true"
                            v-model="itemForm.receiveDate"
                            button-only
                            right
                            size="sm"
                            locale="en-CA"
                            aria-controls="form-input-returnDate"
                            @context="onContext"
                          />
                        </b-input-group-append>
                      </b-input-group>
                    </form>
                  </div>
                  <div class="col-auto header-warehouse">
                    <form @submit.prevent data-vv-scope="itemForm">
                      <span class="small">Restock in warehouse</span>
                      <b-v-select
                        data-testid="return-warehouse"
                        v-model="itemForm.toWarehouseId"
                        v-validate="{ required: true }"
                        :searchable="true"
                        name="form-input-warehouse"
                        :state="validateState('form-input-warehouse')"
                        data-vv-as="Warehouse"
                        :options="warehousesListAll"
                      >
                        <template v-slot:validationMsg>
                          <b-form-invalid-feedback id="form-input-warehouse-feedback">
                            {{ veeErrors.first('form-input-warehouse') }}
                          </b-form-invalid-feedback>
                        </template>
                      </b-v-select>
                    </form>
                  </div>
                  <div class="col-auto" v-if="itemForm.referenceNum">
                    <span class="small">Return Ref #</span>
                    <h5>{{ itemForm.referenceNum }}</h5>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-12 col-sm-9">
          <b-card no-body v-if="itemForm">
            <template #header>
              <h4 class="card-header-title">Items Returned</h4>
            </template>
            <div class="alert alert-warning m-3" v-if="loadingReturnItems">
              <b>Loading return items, please wait...</b>
            </div>
            <div
              class="return-items"
              data-testid="return-items"
              v-else-if="itemForm.items && itemForm.items.length > 0"
            >
              <b-card no-body class="return-item" v-for="item in itemForm.items" :key="item.id || item.sku">
                <template #header>
                  <div class="w-100 d-flex justify-content-between align-items-center">
                    <div class="mr-3">
                      <span class="font-weight-bold mr-2">{{ item.sku }}</span>
                      <span class="text-muted">{{ item.name }}</span>
                    </div>
                    <div class="d-flex align-items-center flex-shrink-0">
                      <b-form-input
                        v-if="item.itemReviewState === 'other'"
                        size="sm"
                        class="review-description-input mr-2"
                        v-model="item.itemReviewDescription"
                        placeholder="Describe why item was rejected..."
                        maxlength="250"
                    :state="IsReviewDescriptionMissing(item) ? false : null"
                        :data-testid="'return-review-description-' + item.sku"
                      />
                      <b-input-group size="sm" prepend="Rejected reason:" class="w-auto flex-nowrap">
                        <b-form-select
                          class="review-state-select"
                          v-model="item.itemReviewState"
                          :disabled="!((parseInt(item.quantityDamaged) || 0) > 0)"
                          :options="ReviewStateOptions(item)"
                          :data-testid="'return-review-state-' + item.sku"
                        />
                      </b-input-group>
                      <b-button
                        size="sm"
                        class="ml-2"
                        :variant="item.isAddedManually ? 'outline-danger' : 'outline-secondary'"
                        :disabled="!item.isAddedManually"
                        :data-testid="'return-remove-' + item.sku"
                        @click="RemoveItem(item)"
                      >
                        <i class="fa fa-times"></i> Remove
                      </b-button>
                    </div>
                  </div>
                </template>
                <div class="card-body row mx-0">
                  <div class="col-12 col-lg-4 mb-3 mb-lg-0">
                    <div class="text-muted small mt-1" v-if="item.returnReason"><b>Customer reason: </b>{{ item.returnReason }}</div>
                    <div class="text-danger small mt-1" v-if="item.isBundleWithoutMapings">
                      This is an item with no known mappings, please first create mappings for this bundle SKU before receiving the return.
                    </div>
                    <div class="text-muted small mt-1" v-if="item.parentSku"><b>Component of Bundle</b>: {{ item.parentSku }}</div>
                  </div>

                  <div class="col-4 col-lg-2">
                    <label class="return-item-label">
                      Returned
                      <i
                        class="fa fa-info-circle ml-1 text-muted"
                        v-b-tooltip.hover
                        title="The number of units the user has sent back in the returned box"
                      ></i>
                    </label>
                    <div v-if="editThisSku === item.sku" class="d-flex">
                      <b-form-input type="number" v-model="item.quantityToReturn" min="0" />
                      <b-button class="ml-2" variant="secondary" size="sm" @click="editThisSku = ''; NormalizeQuantity(item, 'quantityReceived')">
                        <i class="fa fa-save"></i>
                      </b-button>
                    </div>
                    <div v-else class="d-flex align-items-center">
                      <span class="h5 mb-0">{{ item.quantityToReturn }}</span>
                      <b-button variant="link" class="p-0 ml-2" size="sm" @click="editThisSku = item.sku">
                        <i class="fa fa-edit"></i>
                      </b-button>
                    </div>
                  </div>

                  <div class="col-4 col-lg-3">
                    <label class="return-item-label">
                      Restock
                      <i
                        class="fa fa-info-circle ml-1 text-muted"
                        v-b-tooltip.hover
                        title="The QTY that will be restocked in the warehouse."
                      ></i>
                    </label>
                    <b-input-group>
                      <b-form-input
                        type="number"
                        v-model="item.quantityReceived"
                        :data-testid="'return-restock-' + item.sku"
                        min="0"
                        :max="item.quantityToReturn"
                        @input="OnRestockInput(item)"
                        @blur="NormalizeQuantity(item, 'quantityReceived')"
                      />
                      <b-input-group-append>
                        <b-button
                          variant="outline-danger"
                          :disabled="!(parseInt(item.quantityReceived) > 0)"
                          :data-testid="'return-move-to-rejected-' + item.sku"
                          v-b-tooltip.hover
                          title="Move 1 from Restock to Rejected"
                          @click="MoveQuantity(item, 'quantityReceived', 'quantityDamaged')"
                        >
                          <i class="fa fa-angle-right"></i>
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>
                  </div>

                  <div class="col-4 col-lg-3">
                    <label class="return-item-label">
                      Rejected
                      <i
                        class="fa fa-info-circle ml-1 text-muted"
                        v-b-tooltip.hover
                        title="The QTY of units that were rejected, and will not be restocked."
                      ></i>
                    </label>
                    <b-input-group>
                      <b-input-group-prepend>
                        <b-button
                          variant="outline-success"
                          :disabled="!(parseInt(item.quantityDamaged) > 0)"
                          :data-testid="'return-move-to-restock-' + item.sku"
                          v-b-tooltip.hover
                          title="Move 1 from Rejected to Restock"
                          @click="MoveQuantity(item, 'quantityDamaged', 'quantityReceived')"
                        >
                          <i class="fa fa-angle-left"></i>
                        </b-button>
                      </b-input-group-prepend>
                      <b-form-input
                        type="number"
                        v-model="item.quantityDamaged"
                        :data-testid="'return-damaged-' + item.sku"
                        min="0"
                        @input="SyncReviewState(item)"
                        @blur="NormalizeQuantity(item, 'quantityDamaged')"
                      />
                    </b-input-group>
                  </div>
                </div>

                <template #footer>
                  <div class="row">
                    <div class="col-12 col-md-4 mb-2 mb-md-0">
                      <b-input-group size="sm" prepend="Lot #">
                        <b-form-input type="text" v-model="item.lotNumber" />
                      </b-input-group>
                    </div>
                    <div class="col-12 col-md-4 mb-2 mb-md-0">
                      <b-input-group size="sm" prepend="Expires On">
                        <b-form-input type="date" v-model="item.expirationDate" />
                      </b-input-group>
                    </div>
                    <div class="col-12 col-md-4">
                      <b-input-group size="sm" prepend="Value $">
                        <b-form-input type="number" v-model="item.itemStockCost" min="0" />
                      </b-input-group>
                    </div>
                  </div>
                </template>
              </b-card>
            </div>

            <template v-slot:footer>
              <form @submit.prevent name="orderReturnItemForm" id="orderReturnItemForm" data-vv-scope="orderReturnItemForm">
                <div class="alert alert-info p-2" v-if="!itemForm.projectId">
                  <b>Cannot load product list.</b>
                </div>
                <div class="alert alert-warning p-2" v-else-if="loadingProductItems">
                  <b>Loading Products, please wait...</b>
                </div>
                <div class="form-row" v-else>
                  <div class="col-6 col-md-6">
                    <b-form-group label="Pick Product">
                      <b-v-select
                        key="form-iteminput-productId"
                        v-validate="{ required: true }"
                        name="form-iteminput-productId"
                        :state="validateStateScoped('orderReturnItemForm', 'form-iteminput-productId')"
                        :showup="true"
                        v-model="orderReturnItemForm.addProductSku"
                        :searchable="true"
                        :allowNewValues="false"
                        :options="productsList"
                      />
                    </b-form-group>
                  </div>
                  <div class="col-6 col-md-2">
                    <b-form-group label="Returned">
                      <b-form-input
                        type="number"
                        step="1"
                        v-validate="{ required: true, numeric: true, min_value: 1 }"
                        name="form-iteminput-quantityToReturn"
                        :state="validateStateScoped('orderReturnItemForm', 'form-iteminput-quantityToReturn')"
                        v-model="orderReturnItemForm.quantityToReturn"
                        min="1"
                      />
                    </b-form-group>
                  </div>
                  <div class="col-6 col-md-2">
                    <b-form-group label="Restock">
                      <b-form-input
                        type="number"
                        step="1"
                        v-validate="{ required: true, numeric: true, min_value: 1 }"
                        name="form-iteminput-quantityReceived"
                        :state="validateStateScoped('orderReturnItemForm', 'form-iteminput-quantityReceived')"
                        v-model="orderReturnItemForm.quantityReceived"
                        min="1"
                      />
                    </b-form-group>
                  </div>
                  <div class="col-6 col-md-2">
                    <b-form-group label=".">
                      <b-button type="button" block variant="primary" @click="AddOrderItem">Add</b-button>
                    </b-form-group>
                  </div>
                </div>
              </form>
            </template>
          </b-card>
        </div>

        <aside class="col-12 col-sm-3 sticky-aside">
          <b-card header="Actions">
            <b-form-group label="Additional Notes">
              <b-form-textarea v-model="itemForm.notes" rows="4" placeholder="RMA number, condition of the return, or anything else worth noting..." />
            </b-form-group>
            <b-button
              block
              variant="info"
              data-testid="return-save"
              :disabled="savingItem || loadingReturnItems || hasBundleItems"
              @click="SaveOrderReturn"
            >
              <span v-if="loadingReturnItems">Loading...</span>
              <span v-else-if="savingItem">Saving...</span>
              <span v-else>Confirm</span>
            </b-button>
            <ClientBillingLedgerSidebar
              v-model="showClientBillingLedger"
              :related-id="itemForm.id"
              related-type="OrderReturn"
            />
            <router-link
              :disabled="savingItem"
              class="mt-2 btn btn-block btn-outline-danger"
              :to="{ name: 'Storefronts.OrderReturns.Index' }"
            >
              Cancel
            </router-link>
          </b-card>
        </aside>
      </div>
    </section>
</template>

<script>
import { crudMixinGen, myCommonDataMixin } from '@/helpers/utils/vueMixins.js';
import ClientBillingLedgerSidebar from '../../../components/ClientBillingLedgerSidebar.vue';
import OrderReturnStatusBadge from '../../../components/OrderReturnStatusBadge.vue';

export default {
  mixins: [crudMixinGen('returns'), myCommonDataMixin],
  components: { ClientBillingLedgerSidebar, OrderReturnStatusBadge },

  props: {
    orderReturnId: {
      type: String,
      default: '',
      required: false
    }
  },

  data: function() {
    return {
      showClientBillingLedger: false,
      heading: 'Updating Existing Return',
      savingItem: false,
      editThisSku: null,
      loadingReturnItems: false,
      loadingProductItems: false,
      prodsListAll: [],

      reviewStates: [
        { value: 'restockable', text: 'Restockable' },
        { value: 'damaged', text: 'Damaged' },
        { value: 'item-worn-out', text: 'Item worn out' },
        { value: 'used-or-opened', text: 'Used / opened' },
        { value: 'missing-parts', text: 'Missing parts' },
        { value: 'expired', text: 'Expired' },
        { value: 'wrong-item', text: 'Wrong item returned' },
        { value: 'not-as-described', text: 'Not as described' },
        { value: 'packaging-damaged', text: 'Packaging damaged' },
        { value: 'other', text: 'Other' }
      ],

      orderReturnItemForm: {
        addProductSku: null,
        productId: null,
        sku: '',
        name: '',
        quantityToReturn: 1,
        quantityReceived: 1,
        quantityDamaged: 0,
        unitPrice: 0
      }
    };
  },

  computed: {
    productsList: function() {
      var arrs = [];
      this.prodsListAll.forEach(function(itm) {
        arrs.push({ value: itm.id, text: itm.sku + ' (' + itm.name + ')' });
      });
      return arrs;
    },
    hasBundleItems: function() {
      return this.itemForm && this.itemForm.items && this.itemForm.items.some(function(i) { return i.isBundleWithoutMapings; });
    }
  },

  watch: {
    'orderReturnItemForm.addProductSku': function(newVal) {
      var prod = this.prodsListAll.find(function(a) { return a.id === newVal; });
      if (prod) {
        this.orderReturnItemForm.productId = prod.productId;
        this.orderReturnItemForm.sku = prod.sku;
        this.orderReturnItemForm.name = prod.name;
        this.orderReturnItemForm.unitPrice = prod.price;
      } else {
        this.orderReturnItemForm.sku = newVal;
        this.orderReturnItemForm.name = newVal;
        this.orderReturnItemForm.productId = '';
        this.orderReturnItemForm.unitPrice = 0;
      }
    }
  },

  mounted: function() {
    this.ResetForm();
    if (this.orderReturnId) {
      this.LoadOrderReturn();
    }
  },

  methods: {
    onContext: function(ctx) {
      this.itemForm.receiveDate = ctx.selectedYMD;
    },

    LoadOrderReturn: function() {
      var self = this;
      self.loadingReturnItems = true;
      this.$http
        .get('/api/common/orderReturns/forEditing/' + this.orderReturnId)
        .then(function(resp) {
          self.itemForm = resp.data;

          if (self.itemForm.createDateTimeUtc) {
            self.itemForm.createdAt = new Date(self.itemForm.createDateTimeUtc)
              .toISOString()
              .split('T')[0];
          }
          if (self.itemForm.receiveDate) {
            self.itemForm.receiveDate = new Date(self.itemForm.receiveDate)
              .toLocaleDateString('en-CA');
          } else {
            self.itemForm.receiveDate = new Date().toLocaleDateString('en-CA');
          }
          if (self.itemForm.items) {
            self.itemForm.items.forEach(function(item) {
              if (item.expirationDate) {
                item.expirationDate = new Date(item.expirationDate).toLocaleDateString('en-CA');
              }
              item.quantityDamaged = 0;
              self.$set(item, 'itemReviewState', item.itemReviewState || 'restockable');
              self.$set(item, 'itemReviewDescription', item.itemReviewDescription || '');
              self.NormalizeQuantity(item, 'quantityReceived');
            });
          }

          if (self.itemForm.projectId) {
            self.LoadProductsFor(self.itemForm.projectId);
          }
        })
        .catch(function(err) {
          console.error('LoadOrderReturn', err);
          self.$swal('Error', 'Failed to load order return.', 'error');
        })
        .finally(function() {
          self.loadingReturnItems = false;
        });
    },
    LoadProductsFor: function(projectId) {
      var self = this;
      self.loadingProductItems = true;
      this.$http
        .post('/api/common/products/byProject/' + projectId)
        .then(function(resp) {
          self.prodsListAll = resp.data;
          self.loadingProductItems = false;
        })
        .catch(function() {
          self.prodsListAll = [];
          self.loadingProductItems = false;
        });
    },

    AddOrderItem: function() {
      var self = this;
      this.$validator.validateAll('orderReturnItemForm').then(function(result) {
        if (!result) return;

        var addSku = self.orderReturnItemForm.addProductSku;
        if (!addSku) return;

        var prod = self.prodsListAll.find(function(a) { return a.id === addSku; });
        self.itemForm.items.push({
          id: null,
          isAddedManually: true,
          sku: prod ? prod.sku : addSku,
          name: prod ? prod.name : addSku,
          quantityToReturn: parseInt(self.orderReturnItemForm.quantityToReturn),
          quantityReceived: parseInt(self.orderReturnItemForm.quantityReceived),
          quantityDamaged: 0,
          itemReviewState: 'restockable',
          itemReviewDescription: '',
          unitPrice: prod ? prod.price : 0
        });

        self.orderReturnItemForm = {
          addProductSku: null,
          productId: null,
          sku: '',
          name: '',
          quantityToReturn: 1,
          quantityReceived: 1,
          quantityDamaged: 0,
          unitPrice: 0
        };
      });
    },

    NormalizeQuantity: function(item, field) {
      var val = parseInt(item[field]);
      val = isNaN(val) || val < 0 ? 0 : val;
      if (field === 'quantityReceived') {
        val = Math.min(val, parseInt(item.quantityToReturn) || 0);
      }
      this.$set(item, field, val);
      this.SyncReviewState(item);
    },

    OnRestockInput: function(item) {
      var returned = parseInt(item.quantityToReturn) || 0;
      if ((parseInt(item.quantityReceived) || 0) > returned) {
        this.$set(item, 'quantityReceived', returned);
      }
      item.quantityDamaged = Math.max(0, returned - (parseInt(item.quantityReceived) || 0));
      this.SyncReviewState(item);
    },

    IsReviewDescriptionMissing: function(item) {
      return item.itemReviewState === 'other' && !(item.itemReviewDescription || '').trim();
    },

    ReviewStateOptions: function(item) {
      var hasRejected = (parseInt(item.quantityDamaged) || 0) > 0;
      return this.reviewStates.filter(function(o) {
        return !(hasRejected && o.value === 'restockable');
      });
    },

    SyncReviewState: function(item) {
      var hasRejected = (parseInt(item.quantityDamaged) || 0) > 0;
      if (hasRejected && item.itemReviewState === 'restockable') {
        this.$set(item, 'itemReviewState', 'damaged');
      } else if (!hasRejected && item.itemReviewState !== 'restockable') {
        this.$set(item, 'itemReviewState', 'restockable');
      }
    },

    MoveQuantity: function(item, fromField, toField) {
      // A button that becomes disabled never gets mouseleave, which leaves its tooltip stuck open.
      this.$root.$emit('bv::hide::tooltip');
      var from = parseInt(item[fromField]) || 0;
      if (from <= 0) return;
      var to = parseInt(item[toField]) || 0;
      if (toField === 'quantityReceived' && to >= (parseInt(item.quantityToReturn) || 0)) return;
      this.$set(item, fromField, from - 1);
      this.$set(item, toField, to + 1);
      this.SyncReviewState(item);
    },

    RemoveItem: function(item) {
      this.itemForm.items = this.itemForm.items.filter(function(a) {
        return a !== item;
      });
    },

    SaveOrderReturn: function() {
      var self = this;

      if (!self.itemForm.items || self.itemForm.items.length === 0) {
        self.$swal('Validation Error', 'At least one item is required.', 'warning');
        return;
      }

      if (!self.itemForm.toWarehouseId) {
        self.$swal('Validation Error', 'Please pick the warehouse to restock into.', 'warning');
        return;
      }

      var bundleItems = self.itemForm.items.filter(function(i) { return i.isBundleWithoutMapings; });
      if (bundleItems.length > 0) {
        self.$swal('Validation Error', 'Some items are bundles with no known mappings. Please create mappings for all bundle SKUs before confirming.', 'warning');
        return;
      }

      var missingDescriptions = self.itemForm.items.filter(function(i) { return self.IsReviewDescriptionMissing(i); });
      if (missingDescriptions.length > 0) {
        var skus = missingDescriptions.map(function(i) { return i.sku; }).join(', ');
        self.$swal('Validation Error', 'Please describe the item state for items set to "Other": ' + skus, 'warning');
        return;
      }

      self.itemForm.items.forEach(function(item) {
        self.NormalizeQuantity(item, 'quantityReceived');
        self.NormalizeQuantity(item, 'quantityDamaged');
      });

      self.savingItem = true;

      var payload = {
        notes: self.itemForm.notes,
        toWarehouseId: self.itemForm.toWarehouseId,
        receiveDate: self.itemForm.receiveDate,
        carrierCode: self.itemForm.carrierCode,
        serviceCode: self.itemForm.serviceCode,
        originAddress: self.itemForm.originAddress,
        returnAddress: self.itemForm.returnAddress,
        items: (self.itemForm.items || []).map(function(item) {
          return {
            id: item.id || null,
            parentItemId: item.parentItemId || null,
            sku: item.sku,
            name: item.name,
            quantityToReturn: item.quantityToReturn,
            quantityReceived: item.quantityReceived,
            quantityDamaged: item.quantityDamaged,
            itemReviewState: item.itemReviewState,
            itemReviewDescription: item.itemReviewState === 'other' ? item.itemReviewDescription : null,
            lotNumber: item.lotNumber,
            expirationDate: item.expirationDate,
            itemStockCost: item.itemStockCost,
            unitPrice: item.unitPrice
          };
        })
      };

      this.$http
        .put('/api/common/orderReturns/' + self.orderReturnId, payload)
        .then(function() {
          self.$swal('Success!', 'Order return saved and confirmed successfully.', 'success');
          self.$router.push({ name: 'Storefronts.OrderReturns.Index' });
        })
        .catch(function(err) {
          var msg = (err.response && err.response.data && err.response.data.errorMessage) || 'Failed to save order return.';
          self.$swal('Error', msg, 'error');
        })
        .finally(function() {
          self.savingItem = false;
        });
    }
  }
};
</script>

<style scoped>
.return-items {
  padding: 1rem 1.25rem 0;
}
.return-item {
  margin-bottom: 1rem;
  border-radius: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}
.return-item .card-header {
  padding: 0.4rem 1rem;
}
.return-item .card-footer {
  border-bottom-left-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;
}
.review-description-input {
  width: 16rem;
}
.review-state-select {
  width: auto;
  min-width: 11rem;
}
.return-item-label {
  display: block;
  margin-bottom: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #73818f;
}
.header-warehouse {
  min-width: 18rem;
}
.sticky-aside {
  position: sticky;
  top: 4.5rem;
  align-self: flex-start;
}
</style>
